import { useEffect, useState } from 'react';

/**
 * Shared, intuitive schedule builder (CSAGE-266). One friendly cron-free
 * editor — Frequency / day(s) / time / timezone — with an "Advanced (cron)"
 * escape hatch and a human-readable preview. Used by both the report-viewer
 * subscription modal and the admin Subscriptions manager so there is one
 * definition of how a schedule is edited. Users never see or type cron in
 * the simple mode.
 */

export type Freq = 'daily' | 'weekly' | 'monthly';

interface SchedParts {
  freq: Freq;
  hour: number;
  minute: number;
  dows: number[]; // 0=Sun … 6=Sat
  dom: number; // 1..31
}

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const TIMEZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'America/New_York', label: 'Eastern — New York' },
  { value: 'America/Chicago', label: 'Central — Chicago' },
  { value: 'America/Denver', label: 'Mountain — Denver' },
  { value: 'America/Phoenix', label: 'Mountain (no DST) — Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Central Europe — Paris' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

const ORD = (n: number): string => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  const suffix = ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${suffix}`;
};

export function tzLabel(tz: string): string {
  if (!tz) return 'UTC';
  const hit = TIMEZONES.find((t) => t.value === tz);
  if (hit) return hit.label;
  const leaf = tz.split('/').pop() ?? tz;
  return leaf.replace(/_/g, ' ');
}

/** Parse a 5-field cron into the simple builder model, or null if it needs advanced. */
export function parseCron(cron: string): SchedParts | null {
  const p = (cron || '').trim().split(/\s+/);
  if (p.length !== 5) return null;
  const [min, hour, dom, mon, dow] = p as [string, string, string, string, string];
  const int = (f: string): number | null => (/^\d+$/.test(f) ? Number(f) : null);
  const h = int(hour);
  const m = int(min);
  if (h === null || m === null || mon !== '*') return null;
  if (dom === '*' && dow === '*') return { freq: 'daily', hour: h, minute: m, dows: [], dom: 1 };
  if (dom === '*' && dow !== '*') {
    const raw = dow.split(',');
    const dows = raw.map(int).filter((n): n is number => n !== null && n >= 0 && n <= 6);
    if (dows.length !== raw.length) return null;
    return { freq: 'weekly', hour: h, minute: m, dows, dom: 1 };
  }
  if (dow === '*' && dom !== '*') {
    const d = int(dom);
    if (d === null || d < 1 || d > 31) return null;
    return { freq: 'monthly', hour: h, minute: m, dows: [], dom: d };
  }
  return null;
}

export function buildCron(p: SchedParts): string {
  if (p.freq === 'weekly') {
    const d = p.dows.length ? [...p.dows].sort((a, b) => a - b).join(',') : '*';
    return `${p.minute} ${p.hour} * * ${d}`;
  }
  if (p.freq === 'monthly') return `${p.minute} ${p.hour} ${p.dom} * *`;
  return `${p.minute} ${p.hour} * * *`;
}

/** Human-readable rendering of a 5-field cron; falls back to raw for odd shapes. */
export function cronToHuman(cron: string): string {
  const parts = (cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return cron || '—';
  const [min, hour, dom, mon, dow] = parts as [string, string, string, string, string];
  const simple = (f: string): number | null => (/^\d+$/.test(f) ? Number(f) : null);
  const h = simple(hour);
  const m = simple(min);
  const time = h !== null && m !== null ? `${pad(h)}:${pad(m)}` : null;
  if (!time) return cron;
  if (dow !== '*' && dom === '*') {
    const days = dow
      .split(',')
      .map((d) => DAYS[Number(d) % 7])
      .filter(Boolean);
    if (days.length) return `Weekly on ${days.join(', ')} at ${time}`;
  }
  if (dom !== '*' && dow === '*') {
    const d = simple(dom);
    if (d !== null) return `Monthly on the ${ORD(d)} at ${time}`;
  }
  if (dom === '*' && dow === '*' && mon === '*') return `Daily at ${time}`;
  return cron;
}

/**
 * Controlled-ish schedule builder. Seeds internal state from the initial
 * cron/tz, then emits the built cron + tz via onChange on every edit. The
 * `invalid` callback reports the one soft-invalid state (weekly with no day).
 */
export function ScheduleFields({
  cron: initialCron,
  tz: initialTz,
  onChange,
  onValidityChange,
}: {
  cron: string;
  tz: string;
  onChange: (next: { cron: string; tz: string }) => void;
  onValidityChange?: (valid: boolean) => void;
}): JSX.Element {
  const parsed = parseCron(initialCron);
  const [advanced, setAdvanced] = useState(parsed === null);
  const [freq, setFreq] = useState<Freq>(parsed?.freq ?? 'weekly');
  const [time, setTime] = useState(parsed ? `${pad(parsed.hour)}:${pad(parsed.minute)}` : '08:00');
  const [dows, setDows] = useState<number[]>(parsed?.dows.length ? parsed.dows : [1]);
  const [dom, setDom] = useState<number>(parsed?.dom ?? 1);
  const [tz, setTz] = useState(initialTz || 'UTC');
  const [rawCron, setRawCron] = useState(initialCron || '0 8 * * 1');

  const [hh, mm] = time.split(':').map((n) => Number(n));
  const built = advanced
    ? rawCron.trim()
    : buildCron({ freq, hour: hh || 0, minute: mm || 0, dows, dom });
  const weeklyNoDays = !advanced && freq === 'weekly' && dows.length === 0;
  const valid = built.length > 0 && !weeklyNoDays;

  useEffect(() => {
    onChange({ cron: built, tz });
    onValidityChange?.(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [built, tz, valid]);

  const tzOptions = TIMEZONES.some((t) => t.value === tz)
    ? TIMEZONES
    : [{ value: tz, label: tzLabel(tz) }, ...TIMEZONES];

  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="field-label">When</span>
        <button
          type="button"
          className="btn btn--link btn--sm"
          onClick={() => setAdvanced((a) => !a)}
        >
          {advanced ? 'Simple editor' : 'Advanced (cron)'}
        </button>
      </div>

      <p className="text-dim text-sm" aria-live="polite" style={{ margin: 0 }}>
        {weeklyNoDays ? 'Pick at least one day' : cronToHuman(built)} · {tzLabel(tz)}
      </p>

      {advanced ? (
        <div className="grid-2">
          <label className="field" style={{ margin: 0 }}>
            <span className="field-label">Cron expression</span>
            <input
              className="input input--mono"
              value={rawCron}
              onChange={(e) => setRawCron(e.target.value)}
              aria-label="Cron expression"
            />
          </label>
          <TzSelect tz={tz} setTz={setTz} options={tzOptions} />
        </div>
      ) : (
        <div className="grid-2">
          <label className="field" style={{ margin: 0 }}>
            <span className="field-label">Frequency</span>
            <select
              className="select"
              value={freq}
              onChange={(e) => setFreq(e.target.value as Freq)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>

          <label className="field" style={{ margin: 0 }}>
            <span className="field-label">Time</span>
            <input
              type="time"
              className="input"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              aria-label="Time of day"
            />
          </label>

          {freq === 'weekly' && (
            <div className="field" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <span className="field-label">On days</span>
              <div className="chip-group" role="group" aria-label="Days of week">
                {DOW_SHORT.map((d, i) => (
                  <button
                    key={i}
                    type="button"
                    className={'chip' + (dows.includes(i) ? ' chip--on' : '')}
                    aria-pressed={dows.includes(i)}
                    title={d}
                    onClick={() =>
                      setDows((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]))
                    }
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {freq === 'monthly' && (
            <label className="field" style={{ margin: 0 }}>
              <span className="field-label">Day of month</span>
              <select
                className="select"
                value={dom}
                onChange={(e) => setDom(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {ORD(d)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <TzSelect tz={tz} setTz={setTz} options={tzOptions} />
        </div>
      )}
    </div>
  );
}

function TzSelect({
  tz,
  setTz,
  options,
}: {
  tz: string;
  setTz: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}): JSX.Element {
  return (
    <label className="field" style={{ margin: 0 }}>
      <span className="field-label">Timezone</span>
      <select className="select" value={tz} onChange={(e) => setTz(e.target.value)}>
        {options.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </label>
  );
}
